import { isArray, throttle } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { createEncryptor } from "simple-encryptor";
import { v4 as uuidv4 } from "uuid";
import {
  getAchievements,
  getQuizResults,
  getScore,
  setContentId as setContentIdApi,
  setQuizResult,
} from "../api";
import { FileSystem, Screen } from "../components/vs-code";
import { useWorkspaces } from "../components/vs-code/helpers/workspaces";
import { IScreen } from "../components/vs-code/types";
import { Auth } from "./ridersUserContext";
import { DocumentationFilePathProvider } from "./documentationFilePathContext";
import { sessionSocket } from "../singletons/SessionSocket";
import { registerAchievementLocally } from "../utils/pendingAchievements";
import { Logger } from "../utils/logger";


function debounce(func: any, wait: number | undefined) {
  let timeout: string | number | NodeJS.Timeout | undefined;

  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const encryptor = createEncryptor("simpleKeyForSimpleEncryptor");

export const CommunicationContext = React.createContext({});
export const Communication: IScreen = ({ children }) => {
  const {
    readFile,
    readDir,
    writeFile,
    deleteFile,
    getBasePath,
    getFullPath,
    git_push,
    git_commit,
    git_add,
    gitSyncHandler,
    lastCommitHash,
    setLastCommitHash,
    git_get_last_commit_hash,
    git_get_last_commit_info,
    git_status,
    resetOverrides,
    pushFileS3,
    fileModified,
    setFileModified,
  }: any = useContext<any>(FileSystem.context);

  const {
    godotContainerRef,
    documentationRef,
    project,
    activeChapter,
    setActiveChapter,
    chapterKey,
    filePath,
    setFilePath,
    blocklySource,
    setBlocklySource,
    setBlocklyKey,
    yamlData,
  }: any = useContext<any>(Screen.context);
  const { getTabs } = useWorkspaces();

  const [codeSource, setCodeSource] = useState(
    activeChapter?.cells.find((x: any) => x.sourceCode !== undefined)
      ?.sourceCode
  );

  const [triggeredSubmitQuiz, setTriggeredSubmitQuiz] = useState(0);

  const codeData: any = getTabs("code", codeSource);

  const {
    getToken,
    contentId,
    setContentId,
    setHaveOverrides,
    isBranchOwner,
    subChapterClicks,
    shutdown,
    lastSubChapterClickRef,
  }: any = useContext<any>(Auth.context);

  // Use useRef to keep track of current fileModified value
  const fileModifiedRef = useRef(fileModified);

  const subChapterClicksRef = useRef(subChapterClicks);

  const heartbeat = useCallback(() => {
    if (shutdown) return;
    if (!sessionSocket) return;

    sessionSocket.sendHeartbeat();
  }, [shutdown]);

  const debouncedHeartbeat = useMemo(() => {
    return throttle(heartbeat, 30_000, {
      leading: true,
      trailing: false,
    });
  }, [heartbeat]);

  // Update ref whenever fileModified changes
  useEffect(() => {
    fileModifiedRef.current = fileModified;
  }, [fileModified]);

  useEffect(() => {
    subChapterClicksRef.current = subChapterClicks;
  }, [subChapterClicks]);

  const projectLanguage = project.language;

  const [searchParams, setSearchParams] = useSearchParams();
  const setChapter = (_chapters: string, data: any) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      const mainChapter = data?.chapters?.find(
        (_chapter: any) => _chapter.name === _chapters
      );

      if (mainChapter) {
        newParams.set("chapter", mainChapter?.subchapters[0]?.name);
      } else {
        newParams.set("chapter", _chapters);
      }
      return newParams;
    });
  };

  useEffect(() => {
    setCodeSource(
      activeChapter?.cells.find((x: any) => x.sourceCode !== undefined)
        ?.sourceCode
    );

    if (
      !activeChapter?.cells.find((x: any) => x.type === "Code") &&
      !activeChapter?.cells.find((x: any) => x.type === "Simulation")
    ) {
      return;
    }

    if (codeData.path === "blockly.xml") {
      const codeSource =
        activeChapter?.cells.find((x: any) => x.type === "Code")?.sourceCode ||
        activeChapter?.cells.find((x: any) => x.type === "Simulation")
          ?.sourceCode;
      const xmlSource = activeChapter?.cells.find(
        (x: any) => x.type === "Code"
      )?.sourceXml;
      setFilePath(
        projectLanguage === "ino" || projectLanguage === "cpp"
          ? `/usercode/${codeSource}.ino`
          : `/usercode/${codeSource}.py`
      );
      if (xmlSource) setBlocklySource(`${xmlSource}.xml`);
    } else if (
      codeSource &&
      activeChapter?.cells.find((x: any) => x.type === "Code")
    ) {
      readFile(codeData.path).then((code: string) => {
        setFilePath(codeData.path);
      });
    } else {
      if (activeChapter?.cells.find((x: any) => x.type === "Simulation")) {
        const codeSource = activeChapter?.cells.find(
          (x: any) => x.type === "Simulation"
        )?.sourceCode;
        const path =
          projectLanguage === "cpp" || projectLanguage === "ino"
            ? `/usercode/${codeSource}.ino`
            : `/usercode/${codeSource}.py`;
        readFile(path).then((code: string) => {
          setFilePath(path);
        });
      } else {
        setFilePath(
          projectLanguage === "cpp" || projectLanguage === "ino"
            ? "/usercode/user_code.ino"
            : "/usercode/user_code.py"
        );
      }
    }
  }, [activeChapter]);

  const encryptMessage = useCallback((message: any) => {
    if (typeof message === "string") {
      return encryptor.encrypt(message);
    } else if (typeof message === "object") {
      return encryptor.encrypt(JSON.stringify(message));
    }
    return "ERROR";
  }, []);

  const postMessage = useCallback((ref: any, msg: any) => {
    const message = JSON.stringify(msg);

    if (isArray(ref)) {
      ref.forEach((r) => {
        r.current?.contentWindow.postMessage(message, "*");
      });
    } else {
      ref.current?.contentWindow.postMessage(message, "*");
    }
  }, []);

  const parentPostMessage = useCallback((msg: any) => {
    const message = JSON.stringify(msg);
    window.parent.postMessage(message, "*");
  }, []);

  interface ActionMap {
    [key: string]: (...args: any[]) => void;
  }

  const actionMap: ActionMap = {
    listFiles: async function () {
      const files = await readDir("/tmp");

      postMessage([godotContainerRef, documentationRef], {
        action: "init",
        key: getBasePath(),
        data: files,
      });
    },

    fsList: async function (path: string, _?: string) {
      const files = await readDir(path);

      postMessage([godotContainerRef, documentationRef], {
        action: "fsListResponse",
        key: getBasePath(),
        data: files,
        status: "success",
        filePath: path,
      });
    },

    init: async function () {
      this.listFiles();
    },

    getToken: async function (message: any, token: string) {
      postMessage([godotContainerRef, documentationRef], {
        action: "getTokenResponse",
        token: token,
      });
    },

    achieve: async function (message: any, token: any) {
      Logger.log(`Achievement received: ${message.data}`);

      const ws = sessionSocket.ws;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        // Not connected. Register achievement locally but not send to the server.
        registerAchievementLocally(parseInt(message.data));
        Logger.log(`No websocket connection. Achievement ${message.data} registered locally.`);
        return;
      }

      //TODO(Baturay):: What happens if user manually send achievement to the server? 
      ws.send(JSON.stringify({
        type: "achieve",
        achievement_id: parseInt(message.data),
      }));
      Logger.log(`Achievement ${message.data} sent to the server`);

      registerAchievementLocally(parseInt(message.data));
      Logger.log(`Achievement: ${message.data} registered as pending achievement`);


    },

    activity: async function (message: any, token: any) {
      debouncedHeartbeat();
    },

    score_results: async function (message: any, token: any) {
      await getScore(token, encryptMessage(message.data));
    },

    submit_quiz: async function (message: any, token: any) {
      const results = await setQuizResult(
        token,
        message.quiz,
        encryptMessage(message.data)
      );

      setTriggeredSubmitQuiz((prev) => prev + 1);

      postMessage(documentationRef, {
        action: "submit_quiz_response",
        data: results,
      });
    },

    quiz_results: async function (message: any, token: any) {
      const results: any = await getQuizResults(token, message.quiz);
      if (results?.data) {
        setTriggeredSubmitQuiz((prev) => prev + 1);
      }

      postMessage(documentationRef, {
        action: "quiz_results_response",
        data: results.data,
      });
    },

    getachievements: async function (message: any, token: any) {
      // TODO (Etkin): Documentation's ready function is this for now. Fix it.
      const response = { action: "initachievements", data: [] };
      const data = await getAchievements(token);

      if (data.message.includes("Success")) {
        response.data = data.data;
      } else {
        console.error({ action: message.action, message: data.message });
      }

      postMessage(documentationRef, response);
    },

    pushFileToS3: async function (filePath: string) {
      try {
        await pushFileS3(contentId, filePath);
        postMessage([godotContainerRef, documentationRef], {
          action: "pushFileToS3Response",
          filePath: filePath,
          success: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "pushFileToS3Response",
          filePath: filePath,
          success: false,
        });
      }
    },

    gitSyncPing: async function () {
      console.log("gitSyncPing");
      try {
        if (!codeSource) return;
        const code = await readFile(filePath);
        const status = await git_status(filePath);

        // Track simulation start after subject selection
        if (subChapterClicks) {

          if (
            lastSubChapterClickRef.current["simulation-started"] !==
            subChapterClicks.chapterNumber
          ) {
            setFileModified(false);
          }
        }

        if (status === "unmodified") {
          const hash = await git_get_last_commit_hash();
          return postMessage(godotContainerRef, {
            action: "gitSyncPong",
            usercode: code,
            commitHash: hash,
          });
        }

        let message = "";

        if (!isBranchOwner) {
          const [_, _username, filesString] = (
            await git_get_last_commit_info()
          ).message.split(":");
          const f = filesString.split(",");

          const newFiles = new Set([
            ...f.map((i: string) => i.trim()),
            ...filePath.trim(),
          ]);

          message = `_${moment().unix()}:${project.session_user_name
            }:${Array.from(newFiles).join(",")}`;
        }

        await git_add(filePath);
        if (project.dev === "blockly") {
          await git_add(filePath);
        }
        const sha = await git_commit(message);
        postMessage(godotContainerRef, {
          action: "gitSyncPong",
          usercode: code,
          commitHash: sha,
        });
        if (project.dev === "blockly") {
          git_push([filePath]);
        } else {
          git_push([filePath]);
        }
        setLastCommitHash(sha);
      } catch (error) {
        console.error(error);
      }
    },

    readFileContentRequest: async function (filePath: string, _?: string) {
      try {
        const content = await readFile(filePath);
        postMessage([godotContainerRef, documentationRef], {
          action: "readFileContentResponse",
          filePath: filePath,
          content: content,
          success: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "readFileContentResponse",
          filePath: filePath,
          success: false,
        });
      }
    },

    writeFileContentRequest: async function (
      filePath: string,
      content: string
    ) {
      try {
        await writeFile(filePath, content);
        const ls = await readDir("/tmp");
        postMessage([godotContainerRef, documentationRef], {
          action: "writeFileContentResponse",
          filePath: filePath,
          success: true,
          data: ls,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "writeFileContentResponse",
          filePath: filePath,
          success: false,
        });
      }
    },

    deleteFileContentRequest: async function (filePath: string) {
      try {
        await deleteFile(filePath);
        const ls = await readDir("/tmp");
        postMessage([godotContainerRef, documentationRef], {
          action: "deleteFileContentResponse",
          filePath: filePath,
          success: true,
          data: ls,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "deleteFileContentResponse",
          filePath: filePath,
          success: false,
        });
      }
    },

    projectInfoRequest: async function () {
      try {
        postMessage(godotContainerRef, {
          action: "projectInfoResponse",
          project: project,
        });
      } catch (error) {
        postMessage(godotContainerRef, {
          action: "projectInfoResponse",
          project: null,
        });
      }
    },

    syncOverrides: async function () {
      try {
        const info = await gitSyncHandler([".editor"], true);
        postMessage(godotContainerRef, {
          action: "gitSyncResponse",
          status: "success",
          commitHash: "info.hash",
        });
        parentPostMessage({
          action: "gitSyncResponse",
          status: "success",
          commitHash: "info.hash",
        });
      } catch (error) {
        postMessage(godotContainerRef, {
          action: "gitSyncResponse",
          status: "error",
          commitHash: null,
        });
        parentPostMessage({
          action: "gitSyncResponse",
          status: "error",
          commitHash: null,
        });
      }
    },

    resetOverrides: async function () {
      try {
        await resetOverrides();
        postMessage(godotContainerRef, {
          action: "resetOverridesResponse",
          status: "success",
        });
      } catch (error) {
        postMessage(godotContainerRef, {
          action: "resetOverridesResponse",
          status: "error",
        });
        console.error(error);
      }
    },

    getContentId: async function () {
      try {
        postMessage([godotContainerRef, documentationRef], {
          action: "getContentIdResponse",
          contentId: contentId !== "default_content" ? contentId : null,
          success: true,
          status: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "getContentIdResponse",
          contentId: null,
          success: false,
          status: false,
        });
      }
    },

    setContentId: async function (data: any, token: any) {
      try {
        if (data) {
          setContentId(data.contentId);
          await setContentIdApi(token, data.contentId);
        }
        postMessage([godotContainerRef, documentationRef], {
          action: "setContentIdResponse",
          contentId: data.contentId,
          success: true,
          status: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "setContentIdResponse",
          success: false,
          status: false,
        });
      }
    },

    selectContentId: async function (data: any) {
      try {
        await deleteFile(".editor");
        await window.git.add_commit_push("/");
        if (data) {
          setContentId(data.contentId);
        }
        postMessage([godotContainerRef, documentationRef], {
          action: "selectContentIdResponse",
          contentId: data.contentId,
          success: true,
          status: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "selectContentIdResponse",
          success: false,
          status: false,
        });
      }
    },

    clearOverrides: async function () {
      try {
        await deleteFile(".editor");
        await window.git.add_commit_push("/");
        setHaveOverrides(false);
        postMessage([godotContainerRef, documentationRef], {
          action: "clearOverridesResponse",
          success: true,
          status: true,
        });
      } catch (error) {
        postMessage([godotContainerRef, documentationRef], {
          action: "clearOverridesResponse",
          success: false,
          status: false,
        });
      }
    },

    refreshBlockly: async function () {
      setBlocklyKey(uuidv4());
    },

    setChapter: async function (message: any) {
      const { chapter } = message;
      setChapter(chapter, yamlData);
    },

    gitTrigger: debounce(async function () {
      try {
        const info = await gitSyncHandler(["tmp"], true);
        postMessage(godotContainerRef, {
          action: "gitTriggerResponse",
          status: "success",
          commitHash: "info.hash",
        });
        parentPostMessage({
          action: "gitTriggerResponse",
          status: "success",
          commitHash: "info.hash",
        });
      } catch (error) {
        postMessage(godotContainerRef, {
          action: "gitTriggerResponse",
          status: "error",
          commitHash: null,
        });
        parentPostMessage({
          action: "gitTriggerResponse",
          status: "error",
          commitHash: null,
        });
      }
    }, 5000),

    selectUserCode: async function (message: any) {
      const { key } = message;
      setActiveChapter({
        ...activeChapter,
        cells: activeChapter.cells.map((cell: any) => {
          if (cell.type === "Code") {
            const nextCode = cell.multipleSourceCodes?.find(
              (x: any) => x.key === key
            );
            return {
              ...cell,
              sourceCode: nextCode?.sourceCode,
              sourceXml: nextCode?.sourceXml,
            };
          }
          return cell;
        }),
      });
      postMessage(godotContainerRef, {
        action: "selectUserCodeResponse",
        status: "success",
      });
    },

    getAchievementSuccessRate: async function (message: any, token: any) {
      try {
        const data = await getAchievements(token);

        if (data.message.includes("Success") && Array.isArray(data.data)) {
          const total = data.data.length;
          const achieved = data.data.filter(
            (item: any) => item.is_achieved
          ).length;
          const rate = total > 0 ? (achieved / total) * 100 : 0;

          console.log("[getAchievementSuccessRate] Success Rate:", {
            total,
            achieved,
            rate,
          });
          console.log("[getAchievementSuccessRate] Success Rate:", {
            total,
            achieved,
            rate,
          });

          postMessage(godotContainerRef, {
            action: "getAchievementSuccessRateResponse",
            successRate: rate,
            total,
            achieved,
          });
        } else {
          console.error(
            "[getAchievementSuccessRate] Invalid achievement data:",
            data
          );
          postMessage(godotContainerRef, {
            action: "getAchievementSuccessRateResponse",
            error: "Invalid achievement data.",
            successRate: 0,
          });
        }
      } catch (error) {
        console.error("[getAchievementSuccessRate] API request failed:", error);
        postMessage(godotContainerRef, {
          action: "getAchievementSuccessRateResponse",
          error: "API request failed.",
          successRate: 0,
        });
      }
    },
  };

  const godot_iframe_handler = async (action: {
    action: String;
    frame: "gd" | "doc";
    data: any;
  }) => {
    if (action.action === "ready") {
      var result: any = [];

      for (let filename of action.data) {
        result.push({
          name: filename,
          content: await readFile(filename),
        });
      }

      const message = JSON.stringify({
        action: "init",
        key: getBasePath(),
        data: result,
      });

      godotContainerRef.current?.contentWindow.postMessage(message, "*");
    } else if (action.action === "save") {
      for (let item of action.data) {
        writeFile(item.name, item.content);
      }
    }
  };

  const doc_iframe_handler = async (action: {
    action: String;
    frame: "gd" | "doc";
    data: any;
  }) => {
    if (action.action === "ready") {
      var result: any = [];

      for (let filename of action.data) {
        result.push({
          name: filename,
          content: await readFile(filename),
        });
      }

      const message = JSON.stringify({
        action: "init",
        key: getBasePath(),
        data: result,
      });

      documentationRef.current?.contentWindow.postMessage(message, "*");
    } else if (action.action === "save") {
      for (let item of action.data) {
        writeFile(item.name, item.content);
      }
    }
  };

  const messageListener = useCallback(
    async (event: any) => {
      // if (event.origin !== "https://d32lhafmwoak77.cloudfront.net") {
      //   return;
      // }
      // Get actionType and Frame info from the message then take actions.

      const message =
        typeof event.data === "string"
          ? JSON.parse(event.data)
          : typeof event.data === "object"
            ? event.data
            : {};
      var actionType: string = message.action;

      if (
        [
          "readFileContentRequest",
          "writeFileContentRequest",
          "pushFileToS3",
          "fsList",
          "deleteFileContentRequest",
        ].includes(actionType)
      ) {
        actionMap[actionType](message.filePath, message.content);
        return;
      }

      if (
        [
          "init",
          "postScenario",
          "postRobot",
          "gitSyncPing",
          "listFiles",
          "gitTrigger",
          "syncOverrides",
          "resetOverrides",
          "projectInfoRequest",
          "getContentId",
          "selectContentId",
          "clearOverrides",
          "refreshBlockly",
          "setChapter",
        ].includes(message.action)
      )
        actionMap[actionType](message, project);
      else if (
        [
          "achieve",
          "score_results",
          "getachievements",
          "activity",
          "submit_quiz",
          "selectUserCode",
          "quiz_results",
          "getToken",
          "setContentId",
          "getAchievementSuccessRate",
        ].includes(message.action)
      ) {
        return actionMap[actionType](message, getToken());
      }
      // Have we got any scenarios that we have no message.action?
      else if (message.frame) {
        if (message.frame === "gd") godot_iframe_handler(message);
        else doc_iframe_handler(message);
      }
    },
    [filePath, contentId]
  );

  useEffect(() => {
    window.addEventListener("message", messageListener);
    return () => {
      window.removeEventListener("message", messageListener);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, contentId, fileModified, subChapterClicks]);

  return (
    <CommunicationContext.Provider
      value={{
        postMessage,
        parentPostMessage,
        encryptMessage,
        setFilePath,
        filePath,
        blocklySource,
        setBlocklySource,
        actionMap,
        triggeredSubmitQuiz,
      }}
    >
      <DocumentationFilePathProvider>{children}</DocumentationFilePathProvider>
    </CommunicationContext.Provider>
  );
};

Communication.context = CommunicationContext;
